import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, MessageSquarePlus, ShieldCheck, Bot } from 'lucide-react';
import { openDana } from '@/lib/dana';
import { track } from '@/lib/telemetry';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/features/shared/components/ui/sheet';
import { Button } from '@/features/shared/components/ui/button';
import { cn } from '@/lib/utils';
import type { PayslipSummary } from '../types';
import { useMyPayslip, useMyAnalytics } from '../api/hooks';
import { computeCompare } from '../lib/compare';
import { whyDidPayChange, topMover, explainDeductions, anomalies, type Insight } from '../lib/insights';
import { formatMoney, MASK } from '../lib/format';

interface InsightsDrawerProps {
  payslips: PayslipSummary[];
  revealed: boolean;
}

export const InsightsDrawer: React.FC<InsightsDrawerProps> = ({ payslips, revealed }) => {
  const { t } = useTranslation('payroll');
  const a = useMyPayslip(payslips[0]?.id ?? null);
  const b = useMyPayslip(payslips[1]?.id ?? null);
  const analyticsQ = useMyAnalytics(12);
  const [thread, setThread] = useState<Insight[]>([]);

  const currency = a.data?.currency ?? payslips[0]?.currency ?? 'TZS';
  const money = (n: number) => (revealed ? formatMoney(Math.abs(n), currency) : MASK);
  const hasTwo = !!a.data && !!b.data;

  const moverLabel = useMemo(() => {
    if (!hasTwo) return null;
    const cmp = computeCompare(a.data!, b.data!);
    const m = cmp.componentDeltas.find((c) => c.componentType !== 'EMPLOYER_CONTRIBUTION');
    return m?.label ?? null;
  }, [a.data, b.data, hasTwo]);

  const push = (ins: Insight | null) => ins && setThread((t) => [...t, ins]);

  const suggestions: Array<{ label: string; run: () => void; ready: boolean }> = [
    { label: 'Why did my pay change?', ready: hasTwo, run: () => push(whyDidPayChange(a.data!, b.data!)) },
    { label: 'Explain my deductions', ready: !!a.data, run: () => push(explainDeductions(a.data!)) },
    { label: 'Any unusual changes?', ready: !!analyticsQ.data, run: () => push(anomalies(analyticsQ.data!)) },
    ...(moverLabel ? [{ label: `Why did ${moverLabel} change?`, ready: hasTwo, run: () => push(topMover(a.data!, b.data!)) }] : []),
  ];

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" className="gap-2" onClick={() => track('ask_pay_opened')}>
          <Sparkles className="h-4 w-4 text-[#D9711E]" /> {t('myPay.askAboutPay')}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#D9711E]" /> {t('myPay.insightsTitle')}
          </SheetTitle>
          <SheetDescription>{t('myPay.insightsDescription')}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-4 overflow-y-auto py-4">
          {thread.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('myPay.insightsEmpty')}</p>
          )}
          {thread.map((ins) => (
            <div key={ins.id} className="space-y-2 rounded-xl border border-border p-4">
              <p className="flex items-start gap-2 text-sm font-semibold text-charcoal">
                <MessageSquarePlus className="mt-0.5 h-4 w-4 shrink-0 text-[#D9711E]" />
                {ins.question}
              </p>
              <div className="space-y-1 text-sm text-muted-foreground">
                {ins.prose.map((p, i) => <p key={i}>{p}</p>)}
              </div>
              {ins.facts.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {ins.facts.map((f, i) => (
                    <span
                      key={i}
                      className={cn(
                        'rounded-full px-2.5 py-1 text-xs font-medium tabular-nums',
                        f.tone === 'pos' ? 'bg-[#DCFCE7] text-[#166534]' : f.tone === 'neg' ? 'bg-[#FEE2E2] text-[#991B1B]' : 'bg-muted text-charcoal',
                      )}
                    >
                      {f.label}: {money(f.amount)}
                    </span>
                  ))}
                </div>
              )}
              {ins.citations.length > 0 && (
                <p className="text-[11px] text-muted-foreground/70">From: {ins.citations.join(', ')}</p>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-2 border-t border-border pt-3">
          <p className="text-xs font-medium text-muted-foreground">{t('myPay.suggestedQuestions')}</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s.label}
                type="button"
                disabled={!s.ready}
                onClick={s.run}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                  s.ready ? 'border-border text-charcoal hover:bg-muted' : 'border-border text-muted-foreground/50',
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
          {/* Escalation to the real assistant: Dana answers with self-scoped,
              JWT-forwarded payroll/loan/benefits tools and can open a tracked
              support ticket — for anything the local grounded answers can't. */}
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2"
            onClick={() => {
              track('ask_pay_dana');
              openDana();
            }}
          >
            <Bot className="h-4 w-4 text-[#D9711E]" /> {t('myPay.askDana')}
          </Button>
          <p className="flex items-center gap-1.5 pt-1 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3 w-3" /> {t('myPay.insightsSafety')}
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};
